// A crash on a public booking page must never show a blank white screen.
//
// CONSUMER: anonymous public web
//
// Grant, 2026-08-17: "also my booking page does not work" — /book/with/grant
// rendered stark white while another person's page rendered fine. React
// unmounts the WHOLE tree on an uncaught render error, so a client who
// followed an advisor's email-signature link saw nothing at all: no
// header, no phone number, no way forward, and nothing anyone could
// diagnose without a browser console.
//
// This boundary turns that into the worst acceptable outcome instead:
// the Stewardship header, a plain apology, and the office number — the
// page a client can still act on. The technical reason rides along in a
// collapsed "details" block (and console.error) so a teammate looking at
// the same screen can read the actual message without opening dev tools.
//
// Deliberately NOT a retry button: a render that just threw will throw
// again on the same data, and offering a button that does nothing is
// worse than offering the phone.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { BookShell } from "./BookShell";

interface Props {
  children: ReactNode;
}

interface State {
  message: string | null;
}

export class BookErrorBoundary extends Component<Props, State> {
  override state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      message:
        error instanceof Error
          ? `${error.name}: ${error.message}`
          : String(error ?? "Unknown error"),
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // No client PII is in scope here (this tree renders advisor names and
    // times only), so the message and component stack are safe to log.
    console.error("book_page_crashed", {
      message: error instanceof Error ? error.message : String(error),
      componentStack: info.componentStack?.slice(0, 2000) ?? null,
    });
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;
    return (
      <BookShell
        hero={{
          title: "Let's get you booked by phone",
          lede: (
            <>
              Something went wrong loading this page. Call the office at{" "}
              <a href="tel:+16023842604">(602) 384-2604</a> and we&rsquo;ll find a
              time with you right now.
            </>
          ),
        }}
      >
        <div className="book-step">
          <details className="book-card book-quiet">
            <summary>Technical details</summary>
            <code>{this.state.message}</code>
          </details>
        </div>
      </BookShell>
    );
  }
}
